import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { navigateWizard, addPageData } from '../../actions';
import LoanAmountInput from '../../components/LoanAmountInput';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import { safeParseFloat } from '../../lib/helpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import {
  periodPropTypes,
  otherFundsPropTypes,
  loanPeriodAmountPropTypes,
  capOnLoanPropTypes,
} from '../../reducers/propTypes';
import {
  otherFundsSelector,
  periodCurrencySelector,
  periodSelector,
  periodsPerClassIdSelector,
  capOnLoanSelector,
  isFullTimeCourseSelector,
  minLoanAmountSelector,
} from '../../reducers/selectors';

const RemainingPeriodsPage = ({
  dispatchWizardNav,
  dispatchAddPageData,
  otherFunds,
  currentPeriod,
  allPeriods,
  currency,
  remainderLoanPeriod,
  minLoanAmount,
  capOnLoan,
  isFullTimeCourse,
}) => {
  const {
    amount: currentLoanPeriodAmount,
    personalSavings: currentPersonalSavings,
    relativesContribution: currentRelativesContribution,
  } = remainderLoanPeriod;
  const [amount, setAmount] = useState(currentLoanPeriodAmount);
  const [personalSavings, setPersonalSavings] = useState(currentPersonalSavings);
  const [relativesContribution, setRelativesContribution] = useState(currentRelativesContribution);

  const handleSubmit = () => {
    analytics.sendEvent('Click - Next', 'remaining-periods');

    const currentLoanPeriodPayload = payloadFactory.createAddPageDataPayload(
      'remainderLoanPeriod',
      {
        personalSavings,
        relativesContribution,
        amount,
        currency,
      },
      'remainderLoanPeriod',
      {
        personalSavings: `${safeParseFloat(personalSavings, 0)}`,
        relativesContribution: `${safeParseFloat(relativesContribution, 0)}`,
        amount,
        currency,
      },
    );

    dispatchAddPageData(currentLoanPeriodPayload);

    dispatchWizardNav({ dir: 'fwd' });
  };

  const remainderPeriodsCosts = () => {
    const runningTotal = { remainingCOT: 0, remainingCOA: 0 };
    const currentPeriodIndex = () => {
      let currentIndex = 0;
      allPeriods.forEach((period, index) => {
        if (period.periodGuid === currentPeriod.periodGuid) {
          currentIndex = index;
        }
      });
      return currentIndex;
    };

    const totalRemaining = allPeriods.reduce(
      (prev, curr, index) => {
        if (index > currentPeriodIndex()) {
          runningTotal.remainingCOT += curr.costOfTuition;
          runningTotal.remainingCOA += curr.costOfAttendance;
        }
        return runningTotal;
      },
      { runningTotal },
    );

    return {
      parsedRemainderCostOfAttendance: parseFloat(totalRemaining.remainingCOA),
      parsedRemainderCostOfTuition: parseFloat(totalRemaining.remainingCOT),
    };
  };

  const { parsedRemainderCostOfAttendance, parsedRemainderCostOfTuition } = remainderPeriodsCosts();

  const { scholarship, sponsorship, loans } = otherFunds;
  const { remainder: scholarshipRemainder } = scholarship;
  const { amount: scholarshipRemainderAmount } = scholarshipRemainder;

  const { remainder: sponsorshipRemainder } = sponsorship;
  const { amount: sponsorshipRemainderAmount } = sponsorshipRemainder;

  const { remainder: loansRemainder } = loans;
  const { amount: loansRemainderAmount } = loansRemainder;

  const totalOtherFunds = useMemo(() => {
    const parsedScholarshipRemainderAmount = safeParseFloat(scholarshipRemainderAmount, 0);
    const parsedSponsorshipRemainderAmount = safeParseFloat(sponsorshipRemainderAmount, 0);
    const parsedLoansRemainderAmount = safeParseFloat(loansRemainderAmount, 0);

    return (
      parsedScholarshipRemainderAmount +
      parsedSponsorshipRemainderAmount +
      parsedLoansRemainderAmount
    );
  }, [scholarshipRemainderAmount, sponsorshipRemainderAmount, loansRemainderAmount]);

  const involvedCosts = useMemo(
    () => (isFullTimeCourse ? parsedRemainderCostOfAttendance : parsedRemainderCostOfTuition),
    [isFullTimeCourse, parsedRemainderCostOfAttendance, parsedRemainderCostOfTuition],
  );

  const requiredAmount = useMemo(() => involvedCosts - totalOtherFunds, [
    involvedCosts,
    totalOtherFunds,
  ]);

  const { futureMaxLoanAmount } = capOnLoan;

  return (
    <PageLayout title="Let's look at your budget for the rest of your course.">
      <LoanAmountInput
        handleSubmit={handleSubmit}
        currency={currency}
        amount={amount}
        onAmountChange={setAmount}
        personalSavings={personalSavings}
        onPersonalSavingsChange={setPersonalSavings}
        relativesContribution={relativesContribution}
        onRelativesContributionChange={setRelativesContribution}
        minLoanAmount={minLoanAmount}
        maxLoanAmount={futureMaxLoanAmount}
        otherFunds={totalOtherFunds}
        requiredAmount={requiredAmount}
        zeroAmountAllowed
      />
    </PageLayout>
  );
};

RemainingPeriodsPage.propTypes = {
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  currentPeriod: periodPropTypes.isRequired,
  allPeriods: PropTypes.arrayOf(periodPropTypes).isRequired,
  currency: PropTypes.string.isRequired,
  otherFunds: otherFundsPropTypes.isRequired,
  remainderLoanPeriod: loanPeriodAmountPropTypes.isRequired,
  minLoanAmount: PropTypes.number.isRequired,
  capOnLoan: capOnLoanPropTypes.isRequired,
  isFullTimeCourse: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  remainderLoanPeriod: state.builder.ui.remainderLoanPeriod,
  currentPeriod: periodSelector(state),
  allPeriods: periodsPerClassIdSelector(state),
  minLoanAmount: minLoanAmountSelector(state),
  currency: periodCurrencySelector(state),
  otherFunds: otherFundsSelector(state),
  capOnLoan: capOnLoanSelector(state),
  isFullTimeCourse: isFullTimeCourseSelector(state),
});

const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { RemainingPeriodsPage, mapDispatchToProps, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(RemainingPeriodsPage);
